import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { learnerKeys } from './learnerKeys';
import * as submissions from '../../api/submissions';
import type { components } from '../../types/learnerApi';

type ItemAnswerRequest = components['schemas']['ItemAnswerRequest'];

export function useAttempt(assignmentId: string, moduleId: string) {
  const qc = useQueryClient();

  const queryKey = learnerKeys.attempt(assignmentId, moduleId);

  const currentAttempt = useQuery({
    queryKey,
    queryFn: () => submissions.getCurrentAttempt(assignmentId, moduleId),
    retry: (count, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

  const start = useMutation({
    mutationFn: () => submissions.startAttempt(assignmentId, moduleId),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data);
    },
  });

  const patchItem = useMutation({
    mutationFn: (payload: ItemAnswerRequest) =>
      submissions.submitItemAnswer(assignmentId, moduleId, payload),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data);
    },
  });

  const finalize = useMutation({
    mutationFn: () => submissions.finalizeAttempt(assignmentId, moduleId),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data);
      qc.invalidateQueries({ queryKey: learnerKeys.course(assignmentId) });
      qc.invalidateQueries({ queryKey: learnerKeys.dashboard });
      qc.invalidateQueries({ queryKey: learnerKeys.courses });
      qc.invalidateQueries({ queryKey: learnerKeys.points });
    },
  });

  return { currentAttempt, start, patchItem, finalize };
}
